import { Component, Input, OnInit } from '@angular/core'
import { AirbnbChannelService, airbnbDescriptionLocales, airbnbErrorDisplayMessage } from '@tokeet-frontend/channels'
import { Destroyable, Toaster, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { Connection } from '@tv3/store/connection/connection.model'

@Component({
  selector: 'app-airbnb-special-considerations',
  templateUrl: './airbnb-special-considerations.component.html',
})
export class AirbnbSpecialConsiderationsComponent extends Destroyable implements OnInit {
  @Input() connection: Connection
  @Input() isFullSync = false
  @Input() readOnly = false

  locales = airbnbDescriptionLocales
  locale = 'en'
  text = ''
  isLoading = false
  isSaving = false
  loadFailed = false
  private loaded = false
  private savedText = ''
  private loadSequence = 0

  constructor(private airbnbChannelService: AirbnbChannelService, private toaster: Toaster) {
    super()
  }

  get characterCount() {
    return Array.from(this.text).length
  }

  get canSave() {
    return (
      this.loaded &&
      this.isFullSync &&
      !this.readOnly &&
      !this.isLoading &&
      !this.isSaving &&
      this.characterCount <= 300 &&
      this.text !== this.savedText
    )
  }

  get canEdit() {
    return this.loaded && this.isFullSync && !this.readOnly && !this.isLoading && !this.isSaving
  }

  ngOnInit() {
    this.load()
  }

  changeLocale(locale: string) {
    if (this.isSaving || !this.locales.some((item) => item.id === locale)) return
    this.locale = locale
    this.text = ''
    this.load()
  }

  load() {
    if (this.isSaving) return
    const sequence = ++this.loadSequence
    this.loaded = false
    this.isLoading = true
    this.loadFailed = false
    this.airbnbChannelService
      .getSpecialConsiderations(this.params())
      .pipe(untilDestroy(this))
      .subscribe(
        (response) => {
          if (sequence !== this.loadSequence) return
          this.text = this.savedText = response.special_considerations
          this.loaded = true
          this.isLoading = false
        },
        (error) => {
          if (sequence !== this.loadSequence) return
          this.isLoading = false
          this.loadFailed = true
          this.showError(error, $localize`Unable to load special considerations`)
        }
      )
  }

  save() {
    if (!this.canSave) return
    const text = this.text
    this.isSaving = true
    this.airbnbChannelService
      .updateSpecialConsiderations(this.params(), text)
      .pipe(untilDestroy(this))
      .subscribe(
        () => {
          this.savedText = text
          this.isSaving = false
          this.toaster.success($localize`Special considerations saved successfully`)
        },
        (error) => {
          this.isSaving = false
          this.showError(error, $localize`Unable to save special considerations`)
        }
      )
  }

  private params() {
    return {
      channel: this.connection.channelId,
      property: String(this.connection.propertyId),
      room: String(this.connection.roomId),
      locale: this.locale,
    }
  }

  private showError(error: unknown, title: string) {
    this.toaster.error(airbnbErrorDisplayMessage(this.toaster.getError(error)), title)
  }
}
